import { useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Users, Calendar, ExternalLink } from "lucide-react";
import Image from "next/image";
import { z } from "zod";

const StatsSchema = z.object({
	employees: z.number(),
	founded: z.number(),
});

const FounderSchema = z.object({
	name: z.string(),
	title: z.string(),
	company: z.string(),
	location: z.string(),
	image: z.string(),
	description: z.string(),
	stats: StatsSchema,
	highlight: z.string(),
});

const FounderHighlightsSchema = z.object({
	badgeText: z.string().default("Founder Spotlight"),
	header: z.string().default("Meet the Innovators"),
	highlightText: z.string().default("Shaping PropTech's Future"),
	description: z
		.string()
		.default(
			"Discover the stories behind successful PropTech ventures and learn from founders who are transforming the real estate industry."
		),
	keyAchievementText: z.string().default("Key Achievement"),
	employeesLabel: z.string().default("Employees"),
	foundedLabel: z.string().default("Founded"),
	connectButtonText: z.string().default("Connect with"),
	testimonialQuote: z
		.string()
		.default(
			"PropTechBuzz connected me with the right investors and partners. The community's focus on PropTech made all the difference."
		),
	founders: z.array(FounderSchema).default([
		{
			name: "Marcus Rodriguez",
			title: "CEO & Founder",
			company: "BuildTech AI",
			location: "San Francisco, CA",
			image:
				"https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400",
			description:
				"Revolutionizing construction through AI-powered project management. Raised $15M Series A.",
			stats: { employees: 45, founded: 2021 },
			highlight: "Reduced construction delays by 40% using predictive analytics",
		},
		{
			name: "Priya Sharma",
			title: "Co-Founder & CTO",
			company: "SmartRent Solutions",
			location: "London, UK",
			image:
				"https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=400",
			description:
				"IoT-enabled rental management platform serving 50,000+ properties across Europe.",
			stats: { employees: 32, founded: 2020 },
			highlight: "Platform manages $2.1B worth of rental properties",
		},
		{
			name: "David Chen",
			title: "Founder",
			company: "PropInsight Analytics",
			location: "Sydney, Australia",
			image:
				"https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400",
			description: "Data-driven property investment platform with ML-powered market predictions.",
			stats: { employees: 28, founded: 2019 },
			highlight: "95% accuracy in property value predictions",
		},
		{
			name: "Elena Volkov",
			title: "CEO",
			company: "GreenBuild Tech",
			location: "Berlin, Germany",
			image:
				"https://images.pexels.com/photos/1181424/pexels-photo-1181424.jpeg?auto=compress&cs=tinysrgb&w=400",
			description:
				"Sustainable construction materials marketplace connecting eco-friendly suppliers.",
			stats: { employees: 38, founded: 2020 },
			highlight: "Helped reduce carbon footprint by 30% in 200+ projects",
		},
	]),
});

type FounderHighlightsProps = z.input<typeof FounderHighlightsSchema>;

const FounderHighlights: React.FC<FounderHighlightsProps> = (props = {}) => {
	const data = FounderHighlightsSchema.parse(props);
	const [currentSlide, setCurrentSlide] = useState(0);

	const nextSlide = () => {
		setCurrentSlide((prev) => (prev + 1) % data.founders.length);
	};

	const prevSlide = () => {
		setCurrentSlide((prev) => (prev - 1 + data.founders.length) % data.founders.length);
	};

	const currentFounder = data.founders[currentSlide];

	return (
		<section id="founders" className="bg-gradient-to-br from-blue-50 to-teal-50 py-24">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<span className="mb-4 inline-flex items-center rounded-full bg-teal-100 px-4 py-2 text-sm font-medium text-teal-800">
						<Users className="mr-2 h-4 w-4" />
						{data.badgeText}
					</span>
					<h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">
						{data.header}
						<span className="block bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
							{data.highlightText}
						</span>
					</h2>
					<p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600">
						{data.description}
					</p>
				</div>

				<div className="relative">
					<div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
						<div className="grid lg:grid-cols-2">
							<div className="p-8 md:p-12">
								<div className="mb-6 flex items-center">
									<Image
										src={currentFounder.image}
										alt={currentFounder.name}
										className="mr-6 h-20 w-20 rounded-full object-cover"
										width={80}
										height={80}
									/>
									<div>
										<h3 className="text-2xl font-bold text-gray-900">{currentFounder.name}</h3>
										<p className="text-lg text-gray-600">{currentFounder.title}</p>
										<p className="font-semibold text-teal-600">{currentFounder.company}</p>
									</div>
								</div>

								<div className="mb-6 flex items-center text-gray-500">
									<MapPin className="mr-2 h-4 w-4" />
									<span>{currentFounder.location}</span>
								</div>

								<p className="mb-8 text-lg leading-relaxed text-gray-700">
									{currentFounder.description}
								</p>

								<div className="mb-8 rounded-2xl bg-gradient-to-r from-teal-500 to-blue-500 p-6 text-white">
									<h4 className="mb-2 font-semibold">{data.keyAchievementText}</h4>
									<p className="text-teal-100">{currentFounder.highlight}</p>
								</div>

								<div className="mb-8 grid grid-cols-2 gap-6">
									<div className="text-center">
										<div className="mb-2 flex items-center justify-center">
											<Users className="mr-2 h-5 w-5 text-gray-500" />
											<span className="text-2xl font-bold text-gray-900">
												{currentFounder.stats.employees}
											</span>
										</div>
										<p className="text-gray-600">{data.employeesLabel}</p>
									</div>
									<div className="text-center">
										<div className="mb-2 flex items-center justify-center">
											<Calendar className="mr-2 h-5 w-5 text-gray-500" />
											<span className="text-2xl font-bold text-gray-900">
												{currentFounder.stats.founded}
											</span>
										</div>
										<p className="text-gray-600">{data.foundedLabel}</p>
									</div>
								</div>

								<button className="flex items-center font-semibold text-teal-600 transition-colors hover:text-teal-700">
									{data.connectButtonText} {currentFounder.name.split(" ")[0]}
									<ExternalLink className="ml-2 h-4 w-4" />
								</button>
							</div>

							<div className="flex items-center justify-center bg-gradient-to-br from-teal-400 to-blue-500 p-8 md:p-12">
								<div className="text-center text-white">
									<div className="mx-auto mb-6 flex h-32 w-32 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
										<Image
											src={currentFounder.image}
											alt={currentFounder.name}
											className="h-24 w-24 rounded-full object-cover"
											width={96}
											height={96}
										/>
									</div>
									<h4 className="mb-4 text-2xl font-bold">{currentFounder.company}</h4>
									<p className="text-lg text-teal-100">&quot;{data.testimonialQuote}&quot;</p>
								</div>
							</div>
						</div>
					</div>

					<div className="mt-8 flex items-center justify-between">
						<button
							onClick={prevSlide}
							className="rounded-full border border-gray-300 bg-white p-3 shadow-lg transition-all duration-200 hover:bg-gray-50"
						>
							<ChevronLeft className="h-6 w-6 text-gray-600" />
						</button>

						<div className="flex space-x-2">
							{data.founders.map((_, index) => (
								<button
									key={index}
									onClick={() => setCurrentSlide(index)}
									className={`h-3 w-3 rounded-full transition-colors ${
										index === currentSlide ? "bg-teal-600" : "bg-gray-300"
									}`}
								/>
							))}
						</div>

						<button
							onClick={nextSlide}
							className="rounded-full border border-gray-300 bg-white p-3 shadow-lg transition-all duration-200 hover:bg-gray-50"
						>
							<ChevronRight className="h-6 w-6 text-gray-600" />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
};

export default FounderHighlights;
